import { PageActions } from "../../../../../shared/ui/PageActions";
import { ActionRecaptchaNotice } from "../../../../../shared/ui/RecaptchaNotice";
import { usePageTitle } from "../../../../../shared/hooks/usePageTitle";
import { useEffect, useId, useRef } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "../../i18n/useTranslation";
import type { PublicProfessionalProfile } from "../../features/profile/operations";
import { PageHead, PageShell, requestStepFocus } from "../../components/PageShell";
import { AppleMark, GoogleMark } from "../../components/ProviderMarks";
import { StepMeta } from "./StepChrome";
import type { SignOutNotice } from "../../../../../shared/hooks/useSignOutNotice";
import { SessionNoticeLine } from "../../components/SessionNotice";

// Same-origin client sign-in gate. Optional controls appear only when the caller
// supplies working book.* actions, never inert buttons or app.* links.

export type AuthProvider = "apple" | "google";

/**
 * A sign-in attempt that failed.
 *
 * A provider failure has to point at email and password as the way through; a
 * credentials failure has to say nothing about whether the address has an
 * account.
 *
 * DELIBERATE ABANDONMENT IS NOT A FAILURE. A client who closes the Apple sheet
 * changed their mind; BookSignIn swallows those Firebase codes and this screen
 * never sees them.
 *
 * A blocked popup and credential collision have specific safe recovery copy.
 * Neither starts an email-based UID merge.
 */
export type SignInFailure = "provider" | "provider-collision" | "provider-blocked" |
  "provider-unavailable" | "credentials";

interface Props {
  profile: PublicProfessionalProfile;
  required?: boolean;
  email: string;
  password: string;
  /** Null when nothing has failed. */
  failure?: SignInFailure | null;
  /** The provider whose popup is in flight, if any. */
  pendingProvider?: AuthProvider | null;
  /** An email/password attempt is in flight. */
  submitting?: boolean;
  onChange: (field: "email" | "password", value: string) => void;
  onSubmit: () => void;
  /** Present only when same-origin provider sign-in is implemented. */
  onProvider?: (provider: AuthProvider) => void;
  /** An uncertain or completed booking must be resolved before UID changes. */
  identitySwitchBlocked?: boolean;
  /**
   * rc.13 (the Founder's dead-end finding): while the identity switch is
   * blocked, nothing on this screen can be used, so "Back" returns to the
   * booking whose result is pending (Review, where its own screen shows).
   */
  blockedBackHref?: string;
  /** Only render routes owned by book.*; app.* cannot transfer this session. */
  forgotPasswordHref?: string;
  createAccountHref?: string;
  /**
   * Why the client is signed out, when this screen is where they landed
   * after it (shared/hooks/useSignOutNotice.ts): a session that ended
   * unexpectedly, or their own "Use another account". Null otherwise,
   * including on a fresh visit and after a reload.
   */
  signOutNotice?: SignOutNotice | null;
}

// Web v2 tab 08 §01, "1.1 Sign in to book" (approved) and "1.2 With Apple and
// Google" (behind the provider flag; pop-up outcomes are tab 01's).
//
// THE PAGE SHELL, not the booking steps' top bar: wordmark, one 400px column,
// the action at its foot. The comp draws no legal line (`legal={false}`); the
// language switch and cookie settings stay, as on every booking screen.
//
// NO PROGRESS INDICATOR, and that is the design's decision: this is the door
// to a gated professional's flow, not a step of it.
//
// Password recovery is supplied only when Book can safely leave this sign-in.

function StepSignIn({
  profile,
  required = true,
  email,
  password,
  failure = null,
  pendingProvider = null,
  submitting = false,
  onChange,
  onSubmit,
  onProvider,
  identitySwitchBlocked = false,
  blockedBackHref,
  forgotPasswordHref,
  createAccountHref,
  signOutNotice = null,
}: Props) {
  const { t, locale } = useTranslation();
  const fieldId = useId();
  const business = profile.businessName;
  usePageTitle(t("flow.signin.heading", { business }));
  const emailRef = useRef<HTMLInputElement>(null);

  // WITHDRAWN PROVIDERS HAND FOCUS TO EMAIL (QE P3, 2026-09-27). A provider
  // that fails as unconfigured removes both buttons, and the pressed one took
  // focus with it to <body>. The notice says email is the way through, so
  // focus goes there. Only on that transition, and only when focus was lost:
  // someone who had already moved on keeps their place.
  const offered = Boolean(onProvider);
  const wasOffered = useRef(offered);
  useEffect(() => {
    const withdrawn = wasOffered.current && !offered;
    wasOffered.current = offered;
    if (withdrawn && (document.activeElement === null || document.activeElement === document.body)) {
      emailRef.current?.focus();
    }
  }, [offered]);

  // One attempt at a time. The other provider closes while one is in flight so
  // two authentications cannot race.
  const busy = pendingProvider !== null || submitting || identitySwitchBlocked;
  // "Sign in is disabled until both fields have a value."
  const filled = email.trim() !== "" && password !== "";
  const canSubmit = filled && !busy;
  const wrongDetails = failure === "credentials";
  const errorId = `${fieldId}-error`;
  const providerFailure = failure && failure !== "credentials" ? failure : null;

  return (
    <PageShell testId="step-signin" legal={false} meta={<StepMeta />}>
      {/* THE SIGN-OUT LINE, above the heading and focused (SessionNotice.tsx).
          Quiet tone: nothing failed. */}
      <SessionNoticeLine notice={signOutNotice} testId="signin-sign-out-notice" />
      <PageHead
        title={t("flow.signin.heading", { business })}
        sub={t(required ? "flow.signin.intro" : "flow.signin.optionalIntro", { business })}
      />

      {identitySwitchBlocked ? (
        <p className="b2-line t-bad" role="alert">{t("flow.signin.error.bookingPending")}</p>
      ) : null}
      {identitySwitchBlocked && blockedBackHref ? (
        <p className="b2-hint">
          <Link className="b2-text" to={blockedBackHref} data-testid="signin-blocked-back">
            {t("flow.back")}
          </Link>
        </p>
      ) : null}

      {/* Tab 01: the provider's outcome sits under the heading, above the
          buttons. A blocked window is a warning with an instruction, because
          the fix is in the browser rather than in Lura. So is Apple and Google
          being unavailable here (their sign-in could not load: a blocker, a
          network): nothing the person did failed, and right after "You're
          signed out." a red box would read as the sign-out failing (Web
          Director, 2026-10-07). */}
      {providerFailure ? (
        <p
          role="alert"
          className={`b2-line ${providerFailure === "provider-blocked" || providerFailure === "provider-unavailable" ? "t-warn" : "t-bad"}`}
          data-testid="signin-provider-error"
        >
          {t(providerFailure === "provider-collision" ? "flow.signin.error.providerCollision" :
            providerFailure === "provider-blocked" ? "flow.signin.error.providerBlocked" :
              providerFailure === "provider-unavailable" ? "flow.signin.error.providerUnavailable" :
                "flow.signin.error.provider")}
        </p>
      ) : null}

      {onProvider ? (
        <>
          <div className="b2-providers" data-testid="signin-providers">
            <button
              type="button"
              className="b2-prov is-apple"
              data-testid="signin-apple"
              // The PRESSED provider stays focusable (aria-disabled) so focus is
              // not dropped to <body> while its window is open; the other one is
              // locked (reviewer A1). The handler refuses while busy either way.
              disabled={busy && pendingProvider !== "apple"}
              aria-disabled={pendingProvider === "apple" || undefined}
              aria-busy={pendingProvider === "apple" || undefined}
              onClick={() => {
                if (busy) return;
                onProvider("apple");
              }}
            >
              <span className="b2-prov-mark">
                {pendingProvider === "apple" ? <span className="b2-spin" aria-hidden="true" /> : <AppleMark />}
              </span>
              <span>{pendingProvider === "apple" ? t("flow.signin.apple.busy") : t("flow.signin.apple")}</span>
            </button>
            <button
              type="button"
              className="b2-prov is-google"
              data-testid="signin-google"
              // The PRESSED provider stays focusable (aria-disabled) so focus is
              // not dropped to <body> while its window is open; the other one is
              // locked (reviewer A1). The handler refuses while busy either way.
              disabled={busy && pendingProvider !== "google"}
              aria-disabled={pendingProvider === "google" || undefined}
              aria-busy={pendingProvider === "google" || undefined}
              onClick={() => {
                if (busy) return;
                onProvider("google");
              }}
            >
              <span className="b2-prov-mark">
                {pendingProvider === "google" ? <span className="b2-spin" aria-hidden="true" /> : <GoogleMark />}
              </span>
              <span>{pendingProvider === "google" ? t("flow.signin.google.busy") : t("flow.signin.google")}</span>
            </button>
          </div>
          {pendingProvider ? (
            <p className="b2-hint" role="status" data-testid="signin-provider-hint">{t("flow.signin.provider.hint")}</p>
          ) : null}
          <div className="b2-or">{t("flow.signin.or")}</div>
        </>
      ) : null}

      {/*
        A real `<form>` holding the action too: Enter submits from either
        field, and password managers only offer to fill (and save) a
        username/password pair inside one. `noValidate` because this screen's
        own line has to be what a client reads when sign-in fails.
      */}
      <form
        id={`${fieldId}-form`}
        className="b2-form"
        noValidate
        onSubmit={(event) => {
          event.preventDefault();
          if (canSubmit) onSubmit();
        }}
      >
        {/* BOTH FIELDS ARE MARKED, AND BOTH POINT AT THE LINE. A credential
            failure does not say which field is wrong, so marking one would be
            a claim we cannot make. */}
        <div className="b-field">
          <label htmlFor={`${fieldId}-email`}>{t("flow.signin.email.label")}</label>
          <input
            id={`${fieldId}-email`}
            ref={emailRef}
            className={`b-input${wrongDetails ? " is-bad" : ""}`}
            type="email"
            value={email}
            placeholder={t("flow.signin.email.placeholder")}
            autoComplete="username"
            aria-required="true"
            aria-invalid={wrongDetails || undefined}
            aria-describedby={wrongDetails ? errorId : undefined}
            data-testid="signin-email"
            onChange={(event) => onChange("email", event.target.value)}
          />
        </div>

        <div className="b-field">
          <label htmlFor={`${fieldId}-password`}>{t("flow.signin.password.label")}</label>
          <input
            id={`${fieldId}-password`}
            className={`b-input${wrongDetails ? " is-bad" : ""}`}
            type="password"
            value={password}
            placeholder={t("flow.signin.password.placeholder")}
            autoComplete="current-password"
            aria-required="true"
            aria-invalid={wrongDetails || undefined}
            aria-describedby={wrongDetails ? errorId : undefined}
            data-testid="signin-password"
            onChange={(event) => onChange("password", event.target.value)}
          />
        </div>

        {/* Above the button, as the comp draws it. Never says whether the
            address has an account. */}
        {wrongDetails ? (
          <p className="b2-line t-bad" role="alert" id={errorId} data-testid="signin-error">
            {t("flow.signin.error.credentials")}
          </p>
        ) : null}

        <PageActions className="b2-foot">
          <ActionRecaptchaNotice locale={locale} testId="signin-recaptcha" />
          <button
            type="submit"
            form={`${fieldId}-form`}
            className={`b-btn v-primary is-full${canSubmit ? "" : " is-off"}`}
            data-testid="signin-submit"
            // Empty fields: truly disabled, as the comp draws it. Submitting:
            // aria-disabled, so the button that was pressed keeps focus and a
            // wrong-details answer lands next to it (reviewer A1). The form's
            // onSubmit refuses unless canSubmit.
            disabled={!canSubmit && !submitting}
            aria-disabled={submitting || undefined}
            aria-busy={submitting || undefined}
          >
            {submitting ? <span className="b2-spin" aria-hidden="true" /> : null}
            {submitting ? t("flow.signin.submitting") : t("flow.signin.submit")}
          </button>
        </PageActions>
        <div className="b3-account-secondary">
          {forgotPasswordHref ? (
            <Link className="b2-text" to={forgotPasswordHref} data-testid="signin-forgot">
              {t("flow.signin.forgot")}
            </Link>
          ) : null}
          {createAccountHref ? (
            <Link className="b2-text" to={createAccountHref} data-testid="signin-create" onClick={() => requestStepFocus()}>
              {t("flow.signin.create")}
            </Link>
          ) : null}
        </div>
      </form>
    </PageShell>
  );
}

export default StepSignIn;
